import React from "react";
import { ethers } from "ethers";
import { useWeb3 } from "../context/Web3Context";
import { useUserProfile, useTx } from "../hooks/useContract";
import { useNotification } from "../hooks/useNotification";
import { formatUSDT } from "../utils/format";
import Button from "../components/Common/Button";
import FullScreenLoader from "../components/Loader/FullScreenLoader";
import ProgressRing from "../components/Charts/ProgressRing";
import { DAILY_CLUB_NAMES } from "../config/constants";

// Contract now has 7 auto-distributed royalties (no more weekly/monthly clubs).
// Royalty 1 is funded by packages 1 AND 2. Each other royalty N (2-7) is
// funded by package N+1. Eligibility per royalty N: own pkg N+1 + 2 directs in pkg N+1.
function Club() {
  const notify = useNotification();
  const { account, connect, getLogic } = useWeb3();
  const { loading, isRegistered, packages, eligibility, claimable, incomeBreakdown, directRefsCountPerPackage, reload } = useUserProfile();
  const { send, pending } = useTx();

  if (!account) {
    return (
      <div className="empty-state animate-fade-in-up">
        <div className="empty-state-icon">◇</div>
        <div className="empty-state-text mb-lg">Connect wallet.</div>
        <Button variant="primary" onClick={connect}>Connect Wallet</Button>
      </div>
    );
  }
  if (loading) return <FullScreenLoader text="Loading Royalties" />;
  if (!isRegistered) {
    return (
      <div className="empty-state animate-fade-in-up">
        <div className="empty-state-icon">◇</div>
        <div className="empty-state-text">Register first to see royalties.</div>
      </div>
    );
  }

  // Total claimable across all 7 royalties
  const totalClaim = claimable?.total || ethers.BigNumber.from(0);

  const claimOne = async (clubId) => {
    const logic = getLogic();
    if (!logic) return notify.error("Logic contract not configured");
    const rank = DAILY_CLUB_NAMES[clubId] || `Royalty ${clubId}`;
    const ok = await send(() => logic.claimDailyClub(clubId), {
      pendingMsg: `Claiming ${rank}...`,
      successMsg: `${rank} claimed!`,
    });
    if (ok) reload();
  };

  const claimAllRoyalties = async () => {
    const logic = getLogic();
    if (!logic) return notify.error("Logic contract not configured");
    if (totalClaim.isZero()) return notify.warning("Nothing to claim");
    // Loop 1..7 and claim each one that has value
    let claimedCount = 0;
    for (let c = 1; c <= 7; c++) {
      const amt = claimable?.daily?.[c - 1] || ethers.BigNumber.from(0);
      if (amt.isZero()) continue;
      const ok = await send(() => logic.claimDailyClub(c), {
        pendingMsg: `Claiming ${DAILY_CLUB_NAMES[c]}...`,
        successMsg: `${DAILY_CLUB_NAMES[c]} claimed`,
      });
      if (ok) claimedCount++;
    }
    if (claimedCount > 0) reload();
  };

  return (
    <div className="animate-fade-in-up">
      {/* Total claimable hero */}
      <div className="card card-glow mb-lg" style={{ textAlign: "center", padding: 20 }}>
        <div className="stat-card-label">Total Royalty Rewards</div>
        <div
          style={{
            fontFamily: "'Sora', 'Inter', sans-serif",
            fontSize: 38,
            fontWeight: 700,
            background: "var(--grad-cyber)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            lineHeight: 1.1,
            margin: "6px 0",
          }}
        >
          ${formatUSDT(totalClaim)}
        </div>
        <div className="stat-card-sub mb-md">Claimable from all 7 royalties</div>
        <Button
          variant="primary"
          size="lg"
          full
          onClick={claimAllRoyalties}
          loading={pending}
          disabled={totalClaim.isZero()}
        >
          Claim All Royalties
        </Button>
      </div>

      {/* Per-royalty list */}
      <div className="section-header">
        <div className="section-title">Daily Royalties</div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {[1, 2, 3, 4, 5, 6, 7].map((clubId) => {
          const amt = claimable?.daily?.[clubId - 1] || ethers.BigNumber.from(0);
          const rankName = DAILY_CLUB_NAMES[clubId];
          const requiredPkg = clubId + 1;
          const hasPkg = !!packages?.[requiredPkg - 1]?.isActive;
          const directs = directRefsCountPerPackage?.[requiredPkg - 1] || 0;
          // Eligibility criteria — mirrors _isEligibleForDaily() in Global888Logic.
          // The on-chain dailyClubEligibility flag is only refreshed on activation
          // events, so we compute directly from the source-of-truth state instead.
          const isEligible = hasPkg && directs >= 2;

          const directProgress = Math.min(1, directs / 2);
          const packageProgress = hasPkg ? 1 : 0;
          const overallProgress = (directProgress + packageProgress) / 2;

          // Which packages fund this royalty?
          const fundingLabel = clubId === 1 ? "Funded by P1 + P2" : `Funded by P${clubId + 1}`;

          // Concrete lock reason
          let lockReason = "";
          if (!hasPkg && directs < 2) lockReason = `Activate P${requiredPkg} + get 2 directs in P${requiredPkg}`;
          else if (!hasPkg)           lockReason = `Activate P${requiredPkg}`;
          else if (directs < 2)       lockReason = `Get ${2 - directs} more direct${2 - directs === 1 ? "" : "s"} in P${requiredPkg}`;

          return (
            <div key={clubId} className={`card ${isEligible ? "card-glow" : ""}`}>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <ProgressRing
                  value={overallProgress}
                  size={64}
                  thickness={6}
                  color={isEligible ? "var(--cyan)" : "var(--violet)"}
                  label={clubId}
                  sub={`${Math.round(overallProgress * 100)}%`}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3, flexWrap: "wrap" }}>
                    <span style={{ fontFamily: "var(--font-display)", fontSize: 15, fontWeight: 700 }}>
                      {rankName}
                    </span>
                    <span className={`badge ${isEligible ? "badge-green" : "badge-violet"}`} style={{ fontSize: 9 }}>
                      {isEligible ? "ELIGIBLE" : "LOCKED"}
                    </span>
                  </div>
                  <div style={{ fontSize: 10, color: "var(--text-muted)", marginBottom: 3 }}>
                    {fundingLabel}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ color: hasPkg ? "var(--success)" : "var(--text-muted)" }}>
                      {hasPkg ? "✓" : "○"} P{requiredPkg}
                    </span>
                    <span style={{ color: directs >= 2 ? "var(--success)" : "var(--text-muted)" }}>
                      {directs >= 2 ? "✓" : "○"} {directs}/2 directs
                    </span>
                  </div>
                  {!isEligible && lockReason && (
                    <div style={{ fontSize: 10, color: "var(--violet-bright)", marginTop: 4 }}>
                      → {lockReason}
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 }}>
                  <div style={{
                    fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 700,
                    color: !amt.isZero() ? "var(--cyan)" : "var(--text-muted)",
                  }}>
                    ${formatUSDT(amt)}
                  </div>
                  <Button
                    variant={!amt.isZero() ? "primary" : "ghost"}
                    size="sm"
                    loading={pending}
                    disabled={amt.isZero()}
                    onClick={() => claimOne(clubId)}
                  >
                    Claim
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Income summary */}
      <div className="section-header">
        <div className="section-title">Royalty Income</div>
      </div>
      <div className="card">
        <div className="flex justify-between">
          <span className="text-muted">Total Earned via Royalties</span>
          <span className="mono text-neon">
            ${incomeBreakdown ? formatUSDT(incomeBreakdown.dailyClubIncome) : "0.00"}
          </span>
        </div>
      </div>
    </div>
  );
}

export default Club;
